import { ActionReference, GameObjectReference, GameState } from "@shared/types";
import { LitElement, TemplateResult, css, html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { getState, performAction } from "../services/routeService";
import { setEventTimer } from "../utils/timer";

@customElement("game-canvas")
export class GameCanvas extends LitElement {
    public static styles = css`
        .game {
            height: 100%;
            position: relative;
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
        }

        .wrapper {
            margin: 20px 50px;
            display: flex;
            flex-direction: row;
            gap: 20px;
            box-sizing: border-box;
            height: calc(100% - 60px);
        }

        .wrapper > div {
            display: flex;
            flex-direction: column;
        }

        .wrapper > div:first-child {
            display: flex;
            min-width: 65%;
            max-width: 65%;
            height: 100%;
        }

        .header,
        .content {
            height: 100%;
            width: 100%;
            flex-grow: 1;
        }

        .wrapper > div:last-child {
            flex-grow: 1;
        }

        .title {
            text-align: center;
            margin-top: 10px;
        }

        .header {
            position: relative;
            flex-shrink: 10;
        }

        .header > div {
            width: 100%;
            max-height: 100%;
        }

        .header img {
            display: block;
            margin: auto;
            flex-grow: 1;
            height: auto;
            width: 100%;
            max-width: calc(((100svh - 56px) / 2) * (690 / 420));
            max-height: 100%;
            image-rendering: crisp-edges;
            image-rendering: -moz-crisp-edges;
        }

        .header img:nth-child(n + 2) {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }

        .content {
            display: flex;
            flex-direction: column;
            gap: 12px;
            flex-grow: 1;
            overflow: auto;
            margin-top: 10px;
            min-height: 24px;
        }

        .content p {
            margin: 0;
        }

        .footer {
            min-height: 300px;
            background-color: #251a24;
            border: 5px solid #0000;
            border-color: #afafaf #363636 #202020 #919191;
            display: flex;
        }

        .footer .buttons > div {
            display: flex;
            gap: 5px 10px;
            flex-wrap: wrap;
        }

        .footer .actions .button {
            background-color: #948ac2;
        }

        .footer .actions .button:hover {
            background-color: #52478b;
        }
        .footer .actions .button.active {
            background-color: #52478b;
            box-shadow: 0px 0px 0px 3px #52478bb9;
        }

        .footer .buttons {
            display: flex;
            flex-direction: column;
            overflow: auto;
            gap: 20px;
            padding: 10px 15px;
        }

        .footer .button {
            border-radius: 4px;
            background-color: #3e53c9;
            padding: 5px 10px;
            text-transform: uppercase;
            cursor: pointer;
            display: inline-block;
            user-select: none;
        }

        .footer .objects .button {
            width: 100%;
        }

        .footer .button:hover {
            background-color: #1a35ce;
        }

        .audiobutton {
            cursor: pointer;
            background-color: #251a24;
            width: 30px;
            height: 30px;
            margin: auto 0 0 auto;
        }

        .audiobuttonimage {
            width: 15px;
            height: 15px;
        }

        @media screen and (max-width: 1000px) {
            .wrapper {
                flex-direction: column;
                margin: 10px 32px;
            }
            .wrapper > div:first-child {
                width: 100%;
                max-width: unset;
            }
            .footer {
                min-height: unset;
            }
            .content,
            .header {
                height: unset;
                flex-grow: 0;
            }
        }
    `;

    private roomTitle?: string;
    private roomImages?: string[];
    private roomVideos?: string[];
    private roomAudio?: string[];
    private contentText?: string[];
    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];

    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    private typeWriterTimeouts: NodeJS.Timeout[] = [];

    public connectedCallback(): void {
        super.connectedCallback();

        void this.refreshState();
    }

    private async refreshState(): Promise<void> {
        const state: GameState = await getState();

        this.updateState(state);
    }

    private updateState(state: GameState): void {
        // Reset the component
        this.roomTitle = state.roomTitle;
        this.roomImages = state.roomImages;
        this.roomVideos = state.roomVideos;
        this.roomAudio = state.roomAudio;
        this.actionButtons = state.actions;
        this.gameObjectButtons = state.objects;

        this.selectedActionButton = undefined;
        this.selectedGameObjectButtons.clear();

        if (state.text) {
            this.typeWriterTimeouts.forEach(clearTimeout);
            this.typeWriter(state.text);
        }

        this.requestUpdate();
    }

    private typeWriter(text: string[], index: number = 0, charIndex: number = 0, speed: number = 5): void {
        if (index < text.length) {
            if (charIndex < text[index].length) {
                this.contentText = [...text.slice(0, index), text[index].slice(0, charIndex + 1)];
                this.requestUpdate();
                this.typeWriterTimeouts.push(
                    setTimeout(() => this.typeWriter(text, index, charIndex + 1), speed)
                );
            } else {
                this.typeWriterTimeouts.push(setTimeout(() => this.typeWriter(text, index + 1, 0), speed));
            }
        }
    }

    private async handleClickAction(button: ActionReference): Promise<void> {
        if (button.needsObject) {
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();

            this.requestUpdate();
        } else {
            const state: any = await performAction(button.alias);

            if (state === undefined) {
                return;
            }

            this.updateState(state);
        }
    }

    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (!this.selectedActionButton) {
            return;
        }

        this.selectedGameObjectButtons.add(button);

        setEventTimer(
            { actionAlias: this.selectedActionButton.alias, objectAlias: button.alias },
            (reference: ActionReference) => void this.handleClickAction(reference)
        );

        const state: GameState | undefined = await performAction(
            this.selectedActionButton.alias,
            [...this.selectedGameObjectButtons].map((e) => e.alias)
        );

        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }

    private async handleClickAudioSwitch(): Promise<void> {
        await this.handleClickAction({ alias: "switch-audio", label: "Switch Audio", needsObject: false });

        const isMuted: boolean = window.localStorage.getItem("sound") === "muted";
        window.localStorage.setItem("sound", isMuted ? "unmuted" : "muted");

        this.requestUpdate();
    }

    protected render(): TemplateResult {
        return html`
            <div class="game">
                ${this.renderTitle()}
                <div class="wrapper">
                    <div>
                        ${this.renderHeader()}
                        <div>${this.renderVideo()}</div>
                        <div>${this.renderAudio()}</div>
                        ${this.renderContent()}
                    </div>
                    <div>${this.renderFooter()} ${this.renderAudioSwitch()}</div>
                </div>
            </div>
        `;
    }

    private renderTitle(): TemplateResult {
        if (this.roomTitle) {
            return html`<div class="title">${this.roomTitle}</div>`;
        }

        return html`${nothing}`;
    }

    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 0) {
            return html`
                <div class="header">
                    <div>
                        ${this.roomImages?.map(
                            (url) => html`<img width="690" height="420" src="/assets/img/rooms/${url}.png" />`
                        )}
                    </div>
                </div>
            `;
        }
        return html`${nothing}`;
    }

    private renderVideo(): TemplateResult {
        if (this.roomVideos && this.roomVideos.length > 0) {
            return html`
                <div class="video">
                    <div>
                        ${this.roomVideos?.map(
                            (url) =>
                                html`<video
                                    autoplay
                                    width="690"
                                    height="420"
                                    src="/assets/mp4/${url}.mp4"
                                    type="video/mp4"
                                ></video>`
                        )}
                    </div>
                </div>
            `;
        }

        return html`${nothing}`;
    }

    private renderAudio(): TemplateResult {
        if (this.roomAudio && this.roomAudio.length > 0) {
            return html`
                <div class="video">
                    <div>
                        ${this.roomAudio?.map(
                            (url) =>
                                html`<audio class="audio" src="/assets/mp3/${url}.mp3" autoplay loop></audio>`
                        )}
                    </div>
                </div>
            `;
        }

        return html`${nothing}`;
    }

    private renderContent(): TemplateResult {
        return html`<div class="content">${this.contentText?.map((text) => html`<p>${text}</p>`)}</div>`;
    }

    private renderFooter(): TemplateResult {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div class="actions">
                        ${this.actionButtons?.map(
                            (button) => html`<a
                                class="button ${this.selectedActionButton === button ? "active" : ""}"
                                @click=${(): void => void this.handleClickAction(button)}
                                >${button.label}</a
                            >`
                        )}
                    </div>
                    <div class="objects">
                        ${
                            // filter the object buttons to see if action is in their specified actions list. Then map to a useable button.
                            this.selectedActionButton
                                ? this.gameObjectButtons
                                      ?.filter((button) =>
                                          button.actions.includes(this.selectedActionButton!.alias)
                                      )
                                      .map(
                                          (button) => html`<a
                                              class="button ${this.selectedGameObjectButtons.has(button)
                                                  ? "active"
                                                  : ""}"
                                              @click=${(): void => void this.handleClickObject(button)}
                                              >${button.name}</a
                                          >`
                                      )
                                : nothing
                        }
                    </div>
                </div>
            </div>
        `;
    }

    private renderAudioSwitch(): TemplateResult {
        const isMuted: boolean = window.localStorage.getItem("sound") === "muted";
        const buttonImage: string = isMuted ? "mutebutton" : "audiobutton";

        return html`
            <button class="audiobutton" @click=${this.handleClickAudioSwitch}>
                <img class="audiobuttonimage" src="/assets/misc/${buttonImage}.png" />
            </button>
        `;
    }
}
